<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { getAlmInfo } from "@/backend/read/BackendReader";
import NoUserEntry from "@/components/UserSelector/NoUserEntry.vue";
import UserEntry from "@/components/UserSelector/UserEntry.vue";
import AssigneeItemTooltip from "@/components/search/tooltips/AssigneeItemTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListFooter from "@/components/ui/MenuList/MenuListFooter.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSearch from "@/components/ui/MenuList/MenuListSearch.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import type { UserWithForeign } from "@/composables/useUserSearch";
import { useUserSearch } from "@/composables/useUserSearch";
import { useTeamStore } from "@/store/team";
import { isNoUser, unassignedUser } from "@/store/user";

const props = defineProps<{
  selected: readonly string[]; // pip user IDs (backend translates to ALM IDs)
}>();

const emit = defineEmits<{
  change: [assigneeIds: string[]]; // pip user IDs
}>();

const { users, searchQuery } = useUserSearch({
  team: useTeamStore().current,
  includeCurrentUser: true,
  addAlmMapping: true,
});

// Create "Unassigned" option
const unassignedItem: UserWithForeign = {
  ...unassignedUser("#999") /* color-check-ignore */,
  foreign: false,
};

// Internal copy allows user to toggle selections before committing changes on close.
// This provides better UX as the dropdown stays open during multi-selection.
// Store full user objects (not just IDs) so they remain available even after search resets.
const internalSelected = ref<Map<string, UserWithForeign>>(new Map());

// Sync internal state when props change (e.g., when filters are reset externally)
// Only watch props.selected, not users.value, to avoid resetting local changes during search
watch(
  () => props.selected,
  (selectedIds) => {
    const newSelected = new Map<string, UserWithForeign>();
    const availableUsers = [...users.value, unassignedItem];

    for (const userId of selectedIds) {
      const user = availableUsers.find((u) => u.id === userId);
      if (user) {
        newSelected.set(userId, user);
      } else {
        // keep existing user object if we had it (e.g., foreign user after search reset)
        const existingUser = internalSelected.value.get(userId);
        if (existingUser) {
          newSelected.set(userId, existingUser);
        }
      }
    }

    internalSelected.value = newSelected;
  },
  { immediate: true },
);

// All selected users (including "Unassigned") - used for tooltip
const selection = computed(() => [...internalSelected.value.values()]);

// Selected regular users excluding "Unassigned" (which is displayed separately via NoUserEntry)
const selectedRegularUsers = computed(() => selection.value.filter((user) => !isNoUser(user)));

// Search results excluding selected ones to avoid duplicates
const unselectedUsers = computed(() =>
  users.value.filter((user) => !internalSelected.value.has(user.id)),
);

/**
 * Check if user is selected
 */
function isSelected(user: UserWithForeign): boolean {
  return internalSelected.value.has(user.id);
}

/**
 * Toggle user selection
 */
function toggleUser(user: UserWithForeign) {
  if (isUserDisabled(user)) return;

  if (isSelected(user)) {
    internalSelected.value.delete(user.id);
  } else {
    internalSelected.value.set(user.id, user);
  }
}

/**
 * Clear all selections
 */
function clearAll() {
  internalSelected.value = new Map();
}

/**
 * Check if user can be selected (has complete ALM mapping)
 */
function isUserDisabled(user: UserWithForeign): boolean {
  if (isNoUser(user)) return false;
  return user.almMapping !== "complete";
}

const { t } = useI18n();

/**
 * Get tooltip message for disabled users based on ALM mapping status.
 */
function disabledTooltip(user: UserWithForeign): string | undefined {
  const almType = getAlmInfo().name;
  if (user.almMapping === "alm-only") {
    return t("issueSelector.filter.userNotMappedInPip", { almType });
  } else if (user.almMapping === "pip-only") {
    return t("issueSelector.filter.userNotMappedInAlm", { almType });
  }
  return undefined;
}

/**
 * Only emit if selection changed
 */
const handleClose = () => {
  const prevValues = new Set(props.selected);
  const newValues = new Set(internalSelected.value.keys());

  if (prevValues.symmetricDifference(newValues).size) {
    emit("change", [...internalSelected.value.keys()]);
  }

  // Reset search query and results
  searchQuery.value = "";
};
</script>

<template>
  <DropdownMenu
    class="issue-assignee-filter"
    disable-teleport
    stay-open-on-content-click
    width="md"
    @close="handleClose"
  >
    <!-- Trigger -->
    <template #trigger="{ isOpen }">
      <IconButton
        icon="persona/user"
        variant="outlined"
        size="small"
        :activated="isOpen"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :aria-label="
          $t('label.search.filterButton', { value: $t('issueSelector.filter.assignee') })
        "
      >
        <template #tooltip>
          <AssigneeItemTooltip :assignees="selection" />
        </template>
      </IconButton>
      <InfoMarker v-if="selected.length" :top="4" :right="4" />
    </template>

    <MenuList selectable multiselect>
      <MenuListHeader>
        <MenuListTitle>{{ $t("issueSelector.filter.assignee") }}</MenuListTitle>
        <MenuListSearch
          :placeholder="$t('action.searchUsers')"
          :icon-title="$t('label.userSelector.searchTitle')"
          :results-count="users.length"
          @search="searchQuery = $event"
        />
      </MenuListHeader>

      <MenuListItems>
        <NoUserEntry :selected="isSelected(unassignedItem)" @select="toggleUser(unassignedItem)" />

        <!-- Selected users (always visible at top) -->
        <UserEntry
          v-for="user in selectedRegularUsers"
          :key="user.id"
          :user
          :active="true"
          :disabled="isUserDisabled(user)"
          :disabled-tooltip="disabledTooltip(user)"
          @select="() => toggleUser(user)"
        />

        <!-- Unselected users from search results -->
        <UserEntry
          v-for="user in unselectedUsers"
          :key="user.id"
          :user
          :active="false"
          :disabled="isUserDisabled(user)"
          :disabled-tooltip="disabledTooltip(user)"
          @select="() => toggleUser(user)"
        />

        <!-- No results found -->
        <MenuListItem v-if="!users.length && selection.length === 0" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>
      </MenuListItems>

      <MenuListFooter :disabled="internalSelected.size === 0" @click="clearAll" />
    </MenuList>
  </DropdownMenu>
</template>
